import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

export default function OtpVerify() {
  const { verifyOtp } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyOtp(email, code, remember);
      toast.success("Giriş başarılı");
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Kod doğrulanamadı."));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-otp", { email });
      toast.success("Yeni kod gönderildi");
    } catch (err) {
      toast.error("Kod gönderilemedi");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="w-full max-w-[420px]" data-testid="otp-page">
        <div className="mb-10">
          <BrandLogo size="lg" />
        </div>

        <h1 className="text-3xl tracking-tight font-bold text-slate-900 mb-2">Doğrulama Kodu</h1>
        <p className="text-sm text-slate-500 mb-2">
          <span className="text-slate-700 font-medium">{email}</span> adresine 6 haneli bir kod gönderildi. Aşağıya girin.
        </p>
        <p className="text-xs text-slate-400 mb-8">Kod gelmediyse <span className="font-medium text-slate-500">Spam / Öne Çıkanlar</span> klasörünü de kontrol edin.</p>

        <form onSubmit={submit} className="space-y-5">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="123456"
            data-testid="otp-input"
            className="w-full h-14 rounded-lg border border-slate-200 bg-white px-4 text-center text-2xl tracking-[0.5em] font-semibold text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
          />

          {error && (
            <div data-testid="otp-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer select-none" data-testid="otp-remember-label">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              data-testid="otp-remember"
              className="w-4 h-4 accent-slate-900 rounded"
            />
            <span>Bu cihazı 90 gün hatırla (kodu bir daha isteme)</span>
          </label>

          <button
            type="submit"
            disabled={loading || code.length !== 6}
            data-testid="otp-submit"
            className="w-full rounded-full bg-slate-900 text-white hover:bg-slate-800 h-11 font-medium transition-colors duration-150 disabled:opacity-50"
          >
            {loading ? "Doğrulanıyor..." : "Giriş Yap"}
          </button>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={resend}
              data-testid="otp-resend"
              className="text-slate-500 hover:text-slate-900 transition-colors"
            >
              Kodu tekrar gönder
            </button>
            <Link to="/login" className="text-slate-500 hover:text-slate-900 transition-colors" data-testid="otp-back">
              Geri dön
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
